import { AgGridReact } from '@mint-lib/ag-grid/react';
import { useCallback, useMemo, useRef } from 'react';

import {
  ColDef,
  ColumnAutoSizeModule,
  DateFilterModule,
  ICellRendererParams,
  ModuleRegistry,
  PaginationModule,
  RenderApiModule,
  RowSelectionModule,
  SelectionChangedEvent,
  ValueFormatterParams,
} from '@mint-lib/ag-grid/community';
import {
  CellSelectionModule,
  ClientSideRowModelApiModule,
  ClientSideRowModelModule,
  ColumnMenuModule,
  ContextMenuModule,
  LicenseManager,
  RowGroupingModule,
  SetFilterModule,
} from '@mint-lib/ag-grid/enterprise';

import { dateFilterParams } from '../../../helpers/ag-grid-helpers.js';
import { getSchedulerStatus } from '../../../helpers/queryScheduleStatus.js';
import { ScheduleTableType } from '../../../types/queryScheduler.js';
import {
  ActionCell,
  DateCell,
  MessageCell,
  NameCell,
  SchedulingCell,
  StatusCell,
} from './cells/index.js';

ModuleRegistry.registerModules([
  RowGroupingModule,
  ClientSideRowModelApiModule,
  ClientSideRowModelModule,
  RenderApiModule,
  PaginationModule,
  ColumnAutoSizeModule,
  RowGroupingModule,
  RowSelectionModule,
  ContextMenuModule,
  ColumnMenuModule,
  SetFilterModule,
  DateFilterModule,
  CellSelectionModule,
]);

LicenseManager.setLicenseKey(import.meta.env.VITE_AG_GRID_LICENSE_KEY);

type Props = {
  tableData: ScheduleTableType[];
  isLoading: boolean;
  onSelectTableRows: (rows: ScheduleTableType[]) => void;
};

const ScheduleTable = ({ tableData, onSelectTableRows, isLoading }: Props) => {
  const gridRef = useRef<AgGridReact>(null);

  const columnDefs: ColDef[] = useMemo(() => {
    return [
      {
        headerName: 'Name',
        field: 'name',
        filter: !isLoading,
        sortable: !isLoading,
        suppressHeaderMenuButton: isLoading,
        resizable: !isLoading,

        cellRenderer: NameCell,
      },

      {
        headerName: 'Message',
        field: 'message',
        cellRenderer: MessageCell,

        filter: false,
        sortable: !isLoading,
        suppressHeaderMenuButton: isLoading,
        resizable: !isLoading,
      },
      {
        headerName: 'Scheduling',
        field: 'scheduling',
        cellRenderer: SchedulingCell,

        filter: !isLoading,
        sortable: !isLoading,
        suppressHeaderMenuButton: isLoading,
        resizable: !isLoading,
      },
      {
        headerName: 'Status',
        field: 'status',
        cellRenderer: StatusCell,
        filterParams: {
          valueFormatter: (params: ValueFormatterParams) => {
            const statusValue = getSchedulerStatus(params.value).label;

            return statusValue.charAt(0).toUpperCase() + statusValue.slice(1);
          },
        },

        filter: !isLoading,
        sortable: !isLoading,
        suppressHeaderMenuButton: isLoading,
        resizable: !isLoading,
      },
      {
        headerName: 'Start date',
        field: 'startDate',
        cellRenderer: DateCell,
        filterParams: dateFilterParams,

        filter: !isLoading && 'agDateColumnFilter',
        sortable: !isLoading,
        suppressHeaderMenuButton: isLoading,
        resizable: !isLoading,
      },
      {
        headerName: 'End date',
        field: 'endDate',
        cellRenderer: DateCell,
        filterParams: dateFilterParams,

        filter: !isLoading && 'agDateColumnFilter',
        sortable: !isLoading,
        suppressHeaderMenuButton: isLoading,
        resizable: !isLoading,
      },
      {
        headerComponent: () => null,
        field: 'actions',
        pinned: 'right',
        filter: false,
        sortable: false,
        resizable: false,
        width: 40,
        cellRenderer: ActionCell,
      },
    ];
  }, [isLoading]);

  const rowSelection = useMemo(() => {
    return {
      mode: isLoading ? 'singleRow' : 'multiRow',
      hideDisabledCheckboxes: true,
      isRowSelectable: (rowNode: ICellRendererParams<ScheduleTableType>) =>
        !rowNode.data?.__isSkeleton,
    };
  }, [isLoading]);

  const onSelectionChanged = useCallback(
    (event: SelectionChangedEvent) => {
      const selectedNodes = event.api.getSelectedNodes();
      const selectedData = selectedNodes.map((node) => node.data);

      onSelectTableRows(selectedData);
    },
    [onSelectTableRows],
  );

  return (
    <div className="h-full w-full" data-testid="schedule-table">
      <AgGridReact
        ref={gridRef}
        //@ts-expect-error correct type
        rowSelection={rowSelection}
        rowData={tableData}
        columnDefs={columnDefs}
        defaultColDef={{ flex: 1 }}
        rowModelType={'clientSide'}
        paginationAutoPageSize
        onSelectionChanged={onSelectionChanged}
        pagination={!isLoading}
        suppressCellFocus={isLoading}
        suppressRowHoverHighlight={isLoading}
        suppressMovableColumns={isLoading}
        loading={false}
      />
    </div>
  );
};

export { ScheduleTable };
